import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Import i18n configuration
import './i18n';

// Import styles
import './styles/language-transition.css';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider as OriginalNotificationProvider } from './contexts/NotificationContext';
import CustomNotificationProvider from './contexts/CustomNotificationContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { PromptProvider } from './contexts/PromptContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ProjectMenuProvider } from './contexts/ProjectMenuContext';

// Pages
import Login from './pages/Login';
import AccessDenied from './pages/AccessDenied';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/common/ProtectedRoute';
import Register from './pages/Register';
import RequestAccess from './pages/RequestAccess';
import AdminRegistrations from './pages/AdminRegistrations';
import OrganizationDashboard from './pages/OrganizationDashboard';
import AgentConfiguration from './pages/AgentConfiguration';
import ForgotPassword from './pages/ForgotPassword';
import Projects from './pages/Projects';
import CreateProject from './pages/CreateProject';
import ProjectDetail from './pages/ProjectDetail';
import Questionnaire from './pages/Questionnaire';
import WebsiteRequirements from './pages/WebsiteRequirements';
import Users from './pages/Users';
import CreateUser from './pages/CreateUser';
import CreateTeam from './pages/CreateTeam';
import Conversation from './pages/Conversation';
import Documents from './pages/Documents';
import DocumentDetail from './pages/DocumentDetail';
import PRDWorkflow from './pages/PRDWorkflow';
import PRDWorkflowV1 from './pages/PRDWorkflowV1';
import PRDWorkflowEdit from './pages/PRDWorkflowEdit';
import DesignPreview from './pages/DesignPreview';
import Integrations from './pages/Integrations';
import Settings from './pages/Settings';
import SectionSettings from './pages/SectionSettings';
import OrgDocuments from './pages/OrgDocuments';
import Profile from './pages/Profile';
import ProjectList from './pages/ProjectList';
import ProjectDebug from './pages/ProjectDebug';
import Translations from './pages/Translations';
import CreateDialogDemo from './pages/CreateDialogDemo';
import TopicsDialogDemo from './pages/TopicsDialogDemo';
import MyImages from './pages/MyImages';
import ManageTeams from './pages/ManageTeams';
import ProjectUsers from './pages/ProjectUsers';
import ProjectIntegrations from './pages/ProjectIntegrations';
import CreateChildProject from './pages/CreateChildProject';
import PermissionManagement from './pages/PermissionManagement';
import UserPermissionLanding from './pages/UserPermissionLanding';
import CreateRolePage from './pages/CreateRole';
import ManageRolesPage from './pages/ManageRoles';
import CreatePermissionPage from './pages/CreatePermission';
import ManagePermissionsPage from './pages/ManagePermissions';
import TreeComponentTest from './pages/TreeComponentTest';
import ArchivedProjects from './pages/ArchivedProjects';
import UploadedDocuments from './pages/UploadedDocuments';
import TShirtSizeManagement from './pages/TShirtSizeManagement';
import FeedbackManagement from './pages/FeedbackManagement';

// Team Tracking Pages
import TrackingLogin from './pages/tracking/TrackingLogin';
import TrackingDashboard from './pages/tracking/TrackingDashboard';

// Hackathon Pages
import HackathonEvents from './pages/hackathon/HackathonEvents';
import HackathonDashboard from './pages/hackathon/HackathonDashboard';
import HackathonRegistration from './pages/hackathon/HackathonRegistration';
import VolunteerRegistration from './pages/hackathon/VolunteerRegistration';

// Crew Components
import CrewDashboard from './components/prd-workflow/CrewDashboard';
import TechnicalIntegrationPanel from './components/prd-workflow/TechnicalIntegrationPanel';
import MCPIntegrationHub from './components/prd-workflow/MCPIntegrationHub';
import SplitViewProjectInfo from './components/prd-workflow/SplitViewProjectInfo';
import FlowDesigner from './pages/FlowDesigner';
import FlowBuilder from './pages/FlowBuilder';
import OAuthCallback from './pages/OAuthCallback';
import TestOAuthPage from './pages/TestOAuthPage';

// Components
import MainLayout from './components/layout/MainLayout';
import PrivateRoute from './components/auth/PrivateRoute';
import TShirtSizePrompt from './components/common/TShirtSizePrompt';
// import FeedbackPrompt from './components/common/FeedbackPrompt'; // Commented out for local development

// Theme and styles
import theme from './styles/theme';
import './styles/index.css';
import './styles/layout-fixes.css';
import './styles/grid-fixes.css';

// App configuration
export const APP_NAME = "Ailyce";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <OriginalNotificationProvider>
            <PromptProvider>
              <CustomNotificationProvider>
                <LanguageProvider>
                  <Router>
                    <TShirtSizePrompt>
                    {/* <FeedbackPrompt> // Commented out for local development */}
                    <Routes>
                    {/* Home route now shows login page */}
                    <Route path="/" element={<Login />} />

                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />

                    {/* OAuth Callback routes (must be public for OAuth popup) */}
                    <Route path="/oauth/callback/:provider" element={<OAuthCallback />} />

                    {/* OAuth Test Page (public for easy testing) */}
                    <Route path="/test-oauth" element={<TestOAuthPage />} />

                    {/* Standalone preview route — no sidebar, no header, full screen */}
                    <Route path="/projects/:projectId/preview/:taskType" element={
                      <PrivateRoute><DesignPreview /></PrivateRoute>
                    } />

                    {/* Protected routes */}
                    <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>
                      <Route path="/access-denied" element={<AccessDenied />} />
                        <Route path="/projects" element={
                          <ProjectMenuProvider>
                            <Projects />
                          </ProjectMenuProvider>
                        } />
                      <Route path="/projects/create" element={<ProtectedRoute permission="header.createproject"><CreateProject /></ProtectedRoute>} />
                      <Route path="/projects/archived" element={<ArchivedProjects />} />
                      <Route path="/projects/:projectId/new-feature" element={<CreateChildProject />} />
                      <Route path="/projects/:projectId/change-request" element={<CreateChildProject />} />
                      <Route path="/projects/:projectId" element={<ProjectDetail />} />
                      <Route path="/projects/:projectId/questionnaire" element={
                        <ProjectProvider>
                          <Questionnaire />
                        </ProjectProvider>
                      } />
                      <Route path="/projects/:projectId/website-requirements" element={
                        <ProjectProvider>
                          <WebsiteRequirements />
                        </ProjectProvider>
                      } />
                      <Route path="/projects/:projectId/uploaded-documents" element={<UploadedDocuments />} />
                      <Route path="/projects/:projectId/documents" element={<Documents />} />
                      <Route path="/projects/:projectId/documents/:documentId" element={<DocumentDetail />} />
                      <Route path="/projects/:projectId/users" element={<ProjectUsers />} />
                      <Route path="/projects/:projectId/integrations" element={<ProjectIntegrations />} />
                      <Route path="/projects/:projectId/prd-workflow/edit" element={<PRDWorkflowEdit />} />
                      <Route path="/projects/:projectId/prd-workflow/:documentId" element={<PRDWorkflow />} />
                      <Route path="/projects/:projectId/prd-workflow" element={<PRDWorkflow />} />
                      <Route path="/projects/:projectId/prd-workflow-v1" element={<PRDWorkflowV1 />} />
                      <Route path="/projects/:projectId/flow-designer" element={<FlowDesigner />} />
                      <Route path="/projects/:projectId/flow-builder" element={<FlowBuilder />} />
                      <Route path="/projects/:projectId/integrations" element={<Integrations />} />
                      <Route path="/projects/:projectId/crew-dashboard" element={
                        <ProjectProvider>
                          <CrewDashboard />
                        </ProjectProvider>
                      } />
                      <Route path="/projects/:projectId/technical-integration" element={
                        <ProjectProvider>
                          <TechnicalIntegrationPanel />
                        </ProjectProvider>
                      } />
                      <Route path="/projects/:projectId/mcp-integration" element={<MCPIntegrationHub />} />
                      <Route path="/projects/:projectId/project-info" element={
                        <ProjectProvider>
                          <SplitViewProjectInfo />
                        </ProjectProvider>
                      } />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/settings/sections" element={<SectionSettings />} />
                      <Route path="/settings/translations" element={<Translations />} />
                      <Route path="/settings/org-documents" element={<OrgDocuments />} />
                      <Route path="/users" element={<ProtectedRoute permission="admin.user.management"><Users /></ProtectedRoute>} />
                      <Route path="/users/create" element={<CreateUser />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/request-access" element={<RequestAccess />} />
                      <Route path="/admin/registrations" element={<AdminRegistrations />} />
                      <Route path="/admin/organizations" element={<OrganizationDashboard />} />
                      <Route path="/admin/agents" element={<AgentConfiguration />} />
                      <Route path="/admin/tshirt-sizes" element={<TShirtSizeManagement />} />
                      <Route path="/admin/feedback" element={<ProtectedRoute permission="admin.feedback"><FeedbackManagement /></ProtectedRoute>} />
                      <Route path="/teams" element={<ManageTeams />} />
                      <Route path="/teams/create" element={<CreateTeam />} />
                      <Route path="/permissions" element={<ProtectedRoute permission="admin.user.permission"><PermissionManagement /></ProtectedRoute>} />
                      <Route path="/user-permission" element={<ProtectedRoute permission="admin.user.permission"><UserPermissionLanding /></ProtectedRoute>} />
                      <Route path="/roles/create" element={<ProtectedRoute permission="admin.user.permission"><CreateRolePage /></ProtectedRoute>} />
                      <Route path="/roles/manage" element={<ProtectedRoute permission="admin.user.permission"><ManageRolesPage /></ProtectedRoute>} />
                      <Route path="/permissions/create" element={<ProtectedRoute permission="admin.user.permission"><CreatePermissionPage /></ProtectedRoute>} />
                      <Route path="/permissions/manage" element={<ProtectedRoute permission="admin.user.permission"><ManagePermissionsPage /></ProtectedRoute>} />
                      <Route path="/tree-test" element={<TreeComponentTest />} />
                      <Route path="/my-images" element={<MyImages />} />

                      {/* Debug routes */}
                      <Route path="/debug/projects" element={<ProjectList />} />
                      <Route path="/debug/project" element={<ProjectDebug />} />
                      <Route path="/debug/create-dialog" element={<CreateDialogDemo />} />
                      <Route path="/debug/topics-dialog" element={<TopicsDialogDemo />} />
                      
                      {/* Prompt routes - hidden due to fetch failures */}

                      {/* 404 - Page Not Found (inside MainLayout) */}
                      <Route path="*" element={<NotFound />} />
                  </Route>
                  
                  {/* Full-screen conversation route */}
                  <Route path="/projects/:projectId/conversation" element={
                    <PrivateRoute>
                      <ProjectProvider>
                        <Conversation />
                      </ProjectProvider>
                    </PrivateRoute>
                  } />
                  
                  {/* Redirect from /dashboard to /projects for legacy URLs */}
                  <Route path="/dashboard" element={<Navigate to="/projects" replace />} />

                  {/* Team Tracking Routes (separate auth system) */}
                  <Route path="/tracking/login" element={<TrackingLogin />} />
                  <Route path="/tracking/dashboard" element={<TrackingDashboard />} />
                  <Route path="/tracking" element={<Navigate to="/tracking/login" replace />} />

                  {/* Hackathon Routes */}
                  <Route path="/hackathon/register/:registrationId" element={<HackathonRegistration />} />
                  <Route path="/hackathon/volunteer/register/:volunteerId" element={<VolunteerRegistration />} />
                  <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>
                    <Route path="/hackathon" element={<HackathonEvents />} />
                    <Route path="/hackathon/:eventId" element={<HackathonDashboard />} />
                  </Route>

                    {/* Redirect all unmatched routes to login */}
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
                    {/* </FeedbackPrompt> // Commented out for local development */}
                    </TShirtSizePrompt>
                  </Router>
                </LanguageProvider>
              </CustomNotificationProvider>
            </PromptProvider>
          </OriginalNotificationProvider>
        </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
  );
}

export default App;
